import { Box, styled } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import CountrySelectField from 'Components/Forms/CountrySelectField';
import TextInputField from 'Components/Forms/TextInputField';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import PartnerAutocompleteWrapper from 'FeatureModules/Activity/Components/Forms/PartnerAutocompleteWrapper';
import { StyledLink } from 'FeatureModules/Activity/Components/Forms/styles';
import UploadField from 'FeatureModules/DataImport/Components/Forms/UploadField';
import RawMaterialSelection from 'FeatureModules/ComponentChain/Components/Forms/RawMaterialSelection';
import { IGeoUploadFormValues } from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import { IFileConfiguration } from 'FeatureModules/DataImport/types/dataset.types';
import { Field, useFormikContext } from 'formik';
import useConfig from 'hooks/useConfig';
import React, { FC } from 'react';

interface IDatasetUploadFormProps {
  disableOwnerSelection?: boolean;
}

const SelectContainer = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
  background: theme.custom.themeColors.grayScale[5],
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  display: 'flex',
  marginBottom: theme.spacing(2),
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  '& > div': {
    flex: 3,
  },
}));

const UploadContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  '& > div': {
    flex: 2,
  },
}));

const HintContainer = styled('div')(({ theme }) => ({
  background: theme.custom.themeColors.primary[5],
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  height: 'fit-content',
}));

const UploadFieldContainer = styled('div')(({ theme }) => ({
  '& .dropzone-container': {
    marginTop: 12,
  },
}));

const DatasetUploadForm: FC<IDatasetUploadFormProps> = ({ disableOwnerSelection }) => {
  const { geoDataTemplateURL } = useConfig();
  const { values: datasetValues, setValues } = useFormikContext<IGeoUploadFormValues>();
  const { bookGeoDataSupportUrl } = useConfig();

  const handleFileConfigurationChange = (fileConfiguration: IFileConfiguration) => {
    if (!datasetValues) {
      console.error('Dataset values are not set');
      return;
    }
    setValues({
      ...datasetValues,
      fileConfiguration: {
        autoCloseGeojsonGeometries: fileConfiguration.autoCloseGeojsonGeometries,
        csvSeparator:
          fileConfiguration.csvSeparator || datasetValues.fileConfiguration.csvSeparator,
        decimalSeparator:
          fileConfiguration.decimalSeparator || datasetValues.fileConfiguration.decimalSeparator,
      },
    });
  };

  return (
    <>
      <FlexBox mb={2} gap={0.5}>
        <ThemeTypography color="GRAY_60" variant="BODY_SMALL">
          Upload origin geo-data by .csv .xlsx .kml or .geojson.
        </ThemeTypography>
        <ThemeTypography variant="BODY_SMALL">
          <StyledLink to={geoDataTemplateURL}>Download optimal file template examples.</StyledLink>
        </ThemeTypography>
      </FlexBox>

      {/* Dataset content */}
      <SelectContainer>
        <TextInputField
          name="title"
          isRequired
          helperText="Add a name to your geo-dataset that helps you and your colleagues remember to what origin this dataset refers to. Using specific region names could be helpful."
          label="Title"
          size="small"
          placeholder='E.g. "Origin, geo-data Ghana"'
          data-testid="dataset-title-input"
        />
        <RawMaterialSelection
          isRequired
          multiple={false}
          name="rawMaterialId"
          size="small"
          helperText="Select the commodity that is sourced from this origin."
          data-testid="dataset-raw-material-select"
        />
        <CountrySelectField
          isRequired
          showCountryRisk
          name="countryCode"
          label="Country of origin"
          helperText="Select the country where this origin geo-data is located. You can only have one country per origin geo-dataset."
          data-testid="dataset-country-select"
        />
        <PartnerAutocompleteWrapper
          isRequired
          name="ownedBy"
          label="Owner"
          disabled={disableOwnerSelection}
          helperText="Select an owner of this origin geo-dataset. This would optimally be a supplier that is involved in the supply chain connected to this origin. You can also select your own company."
          data-testid="dataset-owner-select"
        />
      </SelectContainer>

      <UploadContainer>
        <UploadFieldContainer>
          <Field
            multiple
            component={UploadField}
            showFileConfiguration
            name="files"
            supportedFileTypes={['xlsx', 'csv', 'kml', 'geojson']}
            fileConfiguration={datasetValues?.fileConfiguration}
            onFileConfigurationChange={handleFileConfigurationChange}
          />
        </UploadFieldContainer>

        <Box display="flex" flexDirection="column" gap={2}>
          <HintContainer>
            <ThemeTypography variant="BODY_MEDIUM" color="BLUE_80">
              Please include accurate coordinates for each farm plot in your geo-data. You can use a
              single point for a plot or multiple points to outline the cultivation areas. Multiple
              files are allowed, but each file must use the same formatting structure.
            </ThemeTypography>
          </HintContainer>

          <HintContainer>
            <ThemeTypography variant="BODY_MEDIUM" color="BLUE_80">
              If you do not have the geo-data in any of the supported file formats, we can also
              allocate resources to help you to manually convert the data to a readable format for
              the deforestation analysis. Set up a meeting with us to discuss this further.
            </ThemeTypography>
            <Box mt={2} />
            <ThemeButton
              color="WHITE"
              startIcon={<Icon name="calendar" />}
              onClick={() => window.open(bookGeoDataSupportUrl, '_blank')}
            >
              Schedule meeting
            </ThemeButton>
          </HintContainer>
        </Box>
      </UploadContainer>
    </>
  );
};

export default DatasetUploadForm;
